<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit">
              {{ $t('billing.subscriptions.subscriptionsLabel') }}
            </div>
          </template>

          <template #end>
            <CustomDropdown
                v-model="selectedTenantTypeFilter"
                :options="tenantTypeFilters"
                :showClear="false"
                :filter="false"
                :preventFilter="true"
                class="system-subscriptions__tenant-type-filter p-mr-2"
                @change="filtersChanged">
              <template #value="slotProps">
                <span>
                  {{ $t(slotProps.value?.labelKey || 'paidSubscriptionsFilterLabel') }}
                </span>
              </template>
              <template #option="slotProps">
                <span>{{ $t(slotProps.option.labelKey) }}</span>
              </template>
            </CustomDropdown>

            <CustomDropdown
                v-model="selectedPlanFilter"
                :options="planOptions"
                :placeholder="$t('billing.subscriptions.system.planFilterPlaceholder')"
                :showClear="true"
                :filter="false"
                :preventFilter="true"
                class="system-subscriptions__filter p-mr-2"
                @change="filtersChanged">
              <template #value="slotProps">
                <span v-if="slotProps.value">{{ slotProps.value.key }}</span>
                <span v-else>{{ slotProps.placeholder }}</span>
              </template>
              <template #option="slotProps">
                <span>{{ slotProps.option.key }}</span>
              </template>
            </CustomDropdown>

            <CustomDropdown
                v-model="selectedStatusFilter"
                :options="statusOptions"
                :placeholder="$t('All Statuses')"
                :showClear="true"
                :filter="false"
                :preventFilter="true"
                class="system-subscriptions__filter p-mr-2"
                @change="filtersChanged">
              <template #value="slotProps">
                <span v-if="slotProps.value"
                      class="state-badge"
                      :class="subscriptionStatusClass(slotProps.value.key)">
                  {{ subscriptionStatusLabel(slotProps.value.key) }}
                </span>
                <span v-else>{{ slotProps.placeholder }}</span>
              </template>

              <template #option="slotProps">
              <span class="state-badge"
                    :class="subscriptionStatusClass(slotProps.option.key)">
                {{ subscriptionStatusLabel(slotProps.option.key) }}
              </span>
              </template>
            </CustomDropdown>

            <SearchInput
                :disabled="dataTableIsLoading"
                v-model="searchData"
                @change-processed-search-value="changeProcessedSearchValue"
                @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>

        <DesktopDataTable
            v-else
            :dataTableIsLoading="dataTableIsLoading"
            :showHeader="false"
            :itemsArrayIsEmpty="itemsArrayIsEmpty"
            :sort="sort"
            :showSearchInput="false"
            :actionButtonsCount="actionButtonsCount"
            @sort-items="sortItems"
            :headers="[
              {name: 'tenant', title: $t('Tenant'), sortable: false},
              {name: 'location', title: $t('Location'), sortable: false},
              {name: 'company', title: $t('Company'), sortable: false},
              {name: 'plan', title: $t('billing.subscriptions.planLabel'), sortable: false},
              {name: 'amount', title: $t('billing.subscriptions.planPriceLabel'), sortable: false},
              {name: 'discount', title: $t('Discount'), sortable: false},
              {name: 'current_period_start', title: $t('billing.invoices.periodStartLabel'), sortable: false},
              {name: 'current_period_end', title: $t('billing.invoices.periodEndLabel'), sortable: false},
              {name: 'pending_plan', title: $t('billing.subscriptions.scheduledPlanLabel'), sortable: false},
              {name: 'status', title: $t('Status'), sortable: false},
              {name: 'updated_at', title: $t('Updated'), sortable: true},
            ]">
          <template #body>
            <tr
                v-for="item of items"
                :key="item.id"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.tenant_name">{{ item.tenant_name }}</span>
                <i v-else class="ti-minus"/>

                <div v-if="selectedTenantTypeFilter?.value == null && item.tenant_type"
                     class="table-secondary-line">
                  <span class="state-badge system-subscriptions__tenant-type"
                        :class="tenantTypeClass(item.tenant_type)">
                    {{ tenantTypeLabel(item.tenant_type) }}
                  </span>
                </div>
              </td>

              <td>
                <span v-if="item.location_name">{{ item.location_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.company_name">{{ item.company_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <strong>{{ item.plan_key }}</strong>
              </td>

              <td>
                <div>{{ formatCurrency(currentAmount(item)) }}</div>
                <div v-if="hasActiveDiscount(item)" class="table-secondary-line">
                  <span class="system-subscriptions__old-price">
                    {{ formatCurrency(item.plan_price || 0) }}
                  </span>
                </div>
              </td>

              <td>
                <template v-if="+item.base_discount_percent > 0">
                  <div>−{{ formatPercent(item.base_discount_percent) }}%</div>
                  <div v-if="item.base_discount_starts_at || item.base_discount_ends_at" class="table-secondary-line">
                    <template v-if="item.base_discount_starts_at">
                      {{ $t('billing.subscriptions.fromDateLabel') }} {{ formatUtcDay(item.base_discount_starts_at) }}
                    </template>
                    <template v-if="item.base_discount_ends_at">
                      <span v-if="item.base_discount_starts_at"> · </span>
                      {{ $t('billing.subscriptions.untilDateLabel') }} {{ formatUtcDay(item.base_discount_ends_at - 1) }}
                    </template>
                  </div>
                  <div v-if="item.base_discount_reason" class="table-secondary-line">
                    {{ item.base_discount_reason }}
                  </div>
                </template>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.current_period_start">
                  {{ formatUtcDay(item.current_period_start) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.current_period_end">
                  {{ formatUtcDay(item.current_period_end - 1) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <template v-if="item.pending_plan_code">
                  <strong>{{ item.pending_plan_key }}</strong>
                  <div v-if="item.pending_plan_starts_at" class="table-secondary-line">
                    {{ $t('billing.subscriptions.fromDateLabel') }} {{ formatUtcDay(item.pending_plan_starts_at) }}
                  </div>
                </template>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <div class="p-d-flex p-flex-column p-ai-start">
                  <div class="state-badge" :class="subscriptionStatusClass(item.status_key)">
                    {{ subscriptionStatusLabel(item.status_key) }}
                  </div>

                  <div v-if="+item.cancel_at_period_end === 1" class="table-secondary-line warning-color">
                    {{ $t('billing.subscriptions.cancellationScheduledLabel') }}
                    <template v-if="item.current_period_end">
                      {{ formatUtcDay(item.current_period_end - 1) }}
                    </template>
                  </div>

                  <div v-else-if="item.lock_reason_key" class="table-secondary-line">
                    {{ lockReasonLabel(item.lock_reason_key) }}
                  </div>
                </div>
              </td>

              <td class="table-date-line">
                <span v-if="item.updated_at || item.created_at">
                  {{ formatDate(item.updated_at || item.created_at) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td v-show="actionButtonsCount">
                <div class="actions">
                  <EditButton :disabled="!showModals || dataTableIsLoading" @click="editItem(item)"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator
            v-show="!itemsArrayIsEmpty"
            :rows="pagination.perPage"
            :currentPage="pagination.currentPage - 1"
            :totalPages="pagination.pageCount"
            :totalRecords="pagination.totalCount"
            :rowsPerPageOptions="[5, 10, 25, 50, 100]"
            :pageLinkSize="11"/>

        <TotalRecordsData
            :pagination="pagination"
            :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <SystemSubscriptionModal
            v-if="showModals"
            :visible="itemModal"
            :item="item"
            :planOptions="planOptions"
            :statusOptions="statusOptions"
            :lockReasonOptions="lockReasonOptions"
            @update-items="updateItems"
            @close="closeItemModal"/>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from '@/mixins/formatMixins'
import httpMixins from '@/mixins/httpMixins'
import datatableMixins from '@/mixins/datatableMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import settings from '@/settings'
import SystemSubscriptionModal from '@/pages/system/components/SystemSubscriptionModal'

export default {
  name: 'SystemSubscriptions',
  components: {
    SystemSubscriptionModal,
  },
  mixins: [
    formatMixins,
    httpMixins,
    datatableMixins,
    showErrorsMixins,
  ],
  data() {
    return {
      apiLink: 'location-subscription',
      routePath: '/subscriptions',
      selectedTenantTypeFilter: {
        key: 'paid',
        value: 1,
        labelKey: 'paidSubscriptionsFilterLabel',
      },

      tenantTypeFilters: [
        {
          key: 'paid',
          value: 1,
          labelKey: 'paidSubscriptionsFilterLabel',
        },
        {
          key: 'trial',
          value: 2,
          labelKey: 'trialSubscriptionsFilterLabel',
        },
        {
          key: 'all',
          value: null,
          labelKey: 'allSubscriptionsFilterLabel',
        },
      ],
      selectedPlanFilter: null,
      selectedStatusFilter: null,
      planOptions: [],
      statusOptions: [],
      lockReasonOptions: [],
      sort: {
        column: 'created_at',
        direction: '-',
      },
    }
  },
  mounted() {
    this.scrollToTop()

    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) {
      return false
    }

    this.checkRouteQuery()
    this.getOptions()
    this.getPageData()
  },
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.getItems()
    },

    async getOptions() {
      try {
        const {status, data} = await httpClient({
          url: `${this.apiLink}/metadata`,
          method: 'post',
        })

        if (status === 200 && data?.success) {
          this.planOptions = Array.isArray(data.plans) ? data.plans : []
          this.statusOptions = Array.isArray(data.statuses) ? data.statuses : []
          this.lockReasonOptions = Array.isArray(data.lock_reasons) ? data.lock_reasons : []
        }
      } catch (err) {
        this.showError(err)
      }
    },

    filtersChanged() {
      this.pagination.currentPage = 1
      this.getItems(true)
    },

    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner || this.itemNotFound) this.isLoading = true

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true

      const ctrl = this.setController('systemSubscriptions')

      try {
        const {status, data, headers} = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.isFocusMode ? null : (this.processedSearchValue ?? null),
            tenant_type: this.selectedTenantTypeFilter?.value ?? null,
            plan_code: this.selectedPlanFilter?.code ?? null,
            subscription_status: this.selectedStatusFilter?.code ?? null,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']

          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount = 1
          }

          this.items = data
          this.itemsReceivedTimestamp = Date.now()

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch (err) {
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false

        if (this._abortors?.systemSubscriptions === ctrl) {
          this._abortors.systemSubscriptions = null
        }
      }
    },

    hasActiveDiscount(item) {
      return +item.discount_active === 1
          && Number(item.base_discount_percent || 0) > 0
    },

    currentAmount(item) {
      const basePrice = Number(item.plan_price || 0)
      const discountPercent = Number(item.base_discount_percent || 0)

      if (!this.hasActiveDiscount(item)) {
        return basePrice
      }

      return Math.round(basePrice * (1 - discountPercent / 100) * 100) / 100
    },

    formatPercent(value) {
      const number = Number(value || 0)

      return Number.isInteger(number)
          ? number
          : number.toFixed(2).replace(/\.00$/, '')
    },

    subscriptionStatusLabel(statusKey) {
      const labels = {
        active: 'billing.subscriptions.statuses.active',
        suspended: 'billing.subscriptions.statuses.suspended',
        canceled: 'billing.subscriptions.statuses.canceled',
      }

      return this.$t(
          labels[statusKey] || 'billing.subscriptions.statuses.unknown'
      )
    },

    subscriptionStatusClass(statusKey) {
      const classes = {
        active: 'system-subscription-state--active',
        suspended: 'system-subscription-state--suspended',
        canceled: 'system-subscription-state--canceled',
      }

      return classes[statusKey] || 'system-subscription-state--unknown'
    },

    lockReasonLabel(reasonKey) {
      const labels = {
        billing_past_due: 'billing.subscriptions.system.lockReasons.billingPastDue',
        trial_expired: 'billing.subscriptions.system.lockReasons.trialExpired',
        manual: 'billing.subscriptions.system.lockReasons.manual',
        payment_failed: 'billing.subscriptions.system.lockReasons.paymentFailed',
        subscription_canceled: 'billing.subscriptions.system.lockReasons.canceled',
      }

      return labels[reasonKey]
          ? this.$t(labels[reasonKey])
          : reasonKey
    },

    tenantTypeLabel(tenantType) {
      const labels = {
        1: 'tenantTypeStandard',
        2: 'tenantTypeTrial',
        3: 'tenantTypeDemo',
        5: 'tenantTypeInternal',
        7: 'tenantTypeTest',
      }

      return labels[Number(tenantType)]
          ? this.$t(labels[Number(tenantType)])
          : String(tenantType)
    },

    tenantTypeClass(tenantType) {
      const classes = {
        1: 'system-tenant-type--standard',
        2: 'system-tenant-type--trial',
        3: 'system-tenant-type--demo',
        5: 'system-tenant-type--internal',
        7: 'system-tenant-type--test',
      }

      return classes[Number(tenantType)] || 'system-tenant-type--unknown'
    },
  },
  computed: {
    actionButtonsCount() {
      return 1
    },
  },
}
</script>

<style scoped lang="scss">
td {
  height: 48px;
}

.system-subscriptions__tenant-type-filter {
  width: 120px;
}

.system-subscriptions__filter {
  width: 190px;
}

.system-subscriptions__tenant-type {
  padding: 2px 7px;
  font-size: 0.72rem;
}

.system-subscriptions__old-price {
  text-decoration: line-through;
}

.table-secondary-line {
  margin-top: 3px;
  font-size: 0.82rem;
}

.system-subscription-state--active {
  background: #c8e6c9;
  color: #256029;
}

.system-subscription-state--suspended {
  background: #ffcdd2;
  color: #c63737;
}

.system-subscription-state--canceled,
.system-subscription-state--unknown {
  background: #e0e0e0;
  color: #495057;
}

.system-tenant-type--standard {
  background: #c8e6c9;
  color: #256029;
}

.system-tenant-type--trial {
  background: #bbdefb;
  color: #0d47a1;
}

.system-tenant-type--demo {
  background: #ffe0b2;
  color: #8a4b08;
}

.system-tenant-type--internal,
.system-tenant-type--test,
.system-tenant-type--unknown {
  background: #e0e0e0;
  color: #495057;
}
</style>
